// Ledger domain helpers — pure formatting + state rules shared by the LOG
// (desktop grid + mobile day list), PLAN and TRENDS surfaces and by the
// inspectors. No React here.

import { addDaysYMD, parseLocalDate } from './date';
import type { WeekDay } from './protocol';
import type { MealAnchor } from './day-data';

/** A selectable ledger cell. `day` is the column header (day summary). */
export type LedgerRow = 'day' | 'weight' | 'lunch' | 'snack' | 'dinner' | 'session';
export const LEDGER_ROWS: readonly LedgerRow[] = ['day', 'weight', 'lunch', 'snack', 'dinner', 'session'];

export type FoodSlot = 'lunch' | 'snack' | 'dinner';
export const FOOD_SLOTS: readonly FoodSlot[] = ['lunch', 'snack', 'dinner'];

export function isFoodSlot(row: LedgerRow | null | undefined): row is FoodSlot {
  return row === 'lunch' || row === 'snack' || row === 'dinner';
}

/** Every workout type → its human label. `Strength` is every strength day
 *  (the coach prescribes the exercises); `Mixed` is coach-only. */
export const SESSION_TYPES: ReadonlyArray<[WeekDay['type'], string]> = [
  ['Strength', 'Strength'],
  ['Run-Easy', 'Easy run · Z2'],
  ['Run-Long', 'Long run · Z2'],
  ['Run-Tempo', 'Tempo run · Z3'],
  ['Run-Intervals', 'Intervals · Z4'],
  ['Run-Recovery', 'Recovery run · Z1'],
  ['Padel', 'Padel'],
  ['Rest', 'Rest'],
  ['Mixed', 'Mixed session'],
];

/** The operator-selectable slot types (the SLOT Seg): strength, the runs,
 *  padel, rest. `Mixed` is never picked by hand. */
export const PICKABLE_SESSION_TYPES: ReadonlyArray<[WeekDay['type'], string]> = SESSION_TYPES.filter(
  ([t]) => t !== 'Mixed',
);

/** Short picker label: `RUN EASY`, `STRENGTH`, `PADEL`. */
export function sessionShortLabel(type: string): string {
  return type.replace('Run-', 'RUN ').toUpperCase();
}

/** Human session label. Coach-sourced rows pass their prescription name as
 *  `coachName`, which wins over the vocabulary label. */
export function sessionLabel(type: string | undefined, coachName?: string | null): string {
  if (!type) return '—';
  const name = coachName?.trim();
  if (name) return name;
  return SESSION_TYPES.find((s) => s[0] === type)?.[1] ?? type;
}

const DOW = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;
const MON = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'] as const;

/** `FRI 18 SEP` */
export function dayLabel(iso: string): string {
  const d = parseLocalDate(iso);
  return `${DOW[(d.getDay() + 6) % 7]} ${d.getDate()} ${MON[d.getMonth()]}`;
}

/** `FRI` */
export function dowLabel(iso: string): string {
  return DOW[(parseLocalDate(iso).getDay() + 6) % 7]!;
}

/** `18 SEP` */
export function dateLabel(iso: string): string {
  const d = parseLocalDate(iso);
  return `${d.getDate()} ${MON[d.getMonth()]}`;
}

/** ISO-8601 week number of the Monday `weekStart`. */
export function isoWeekNumber(weekStart: string): number {
  const d = parseLocalDate(weekStart);
  // Thursday of the same week decides the ISO year.
  const thu = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 3);
  const jan1 = new Date(thu.getFullYear(), 0, 1);
  return Math.floor((thu.getTime() - jan1.getTime()) / 86_400_000 / 7) + 1;
}

/** `WEEK 38 · 14 – 20 SEP` (or `14 SEP – 4 OCT`-style across a month). */
export function weekLabel(weekStart: string): string {
  const end = addDaysYMD(weekStart, 6);
  const a = parseLocalDate(weekStart);
  const b = parseLocalDate(end);
  const range =
    a.getMonth() === b.getMonth()
      ? `${a.getDate()} – ${b.getDate()} ${MON[b.getMonth()]}`
      : `${a.getDate()} ${MON[a.getMonth()]} – ${b.getDate()} ${MON[b.getMonth()]}`;
  return `WEEK ${isoWeekNumber(weekStart)} · ${range}`;
}

/** The seven dates of the week starting on `weekStart` (a Monday). */
export function weekDates(weekStart: string): string[] {
  return [0, 1, 2, 3, 4, 5, 6].map((i) => addDaysYMD(weekStart, i));
}

/** One decimal, no trailing zero: 93.1 → "93.1", 93 → "93". */
export function fmt1(n: number): string {
  return (Math.round(n * 10) / 10).toString();
}

/** Signed one-decimal delta with a typographic minus: "−0.4" / "+0.1". */
export function fmtDelta(n: number): string {
  const r = Math.round(n * 10) / 10;
  if (r > 0) return `+${r}`;
  if (r < 0) return `−${Math.abs(r)}`;
  return '0';
}

/** `mm:ss` under an hour, `h:mm:ss` above. */
export function fmtDur(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const x = s % 60;
  return h
    ? `${h}:${String(m).padStart(2, '0')}:${String(x).padStart(2, '0')}`
    : `${String(m).padStart(2, '0')}:${String(x).padStart(2, '0')}`;
}

/** Whole minutes: 2112s → "35". */
export function fmtMinutes(seconds: number): string {
  return String(Math.round(seconds / 60));
}

/** `5:41/km` from seconds per km. */
export function fmtPace(sPerKm: number): string {
  const s = Math.round(sPerKm);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}/km`;
}

/** `HH:MM` local time from an epoch ms. */
export function fmtClock(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// ── Meal timing rules ─────────────────────────────────────────────────────

/** Today's meals are muted until 90 minutes before their scheduled time. */
export const MEAL_LEAD_MIN = 90;

export type MealCellState = 'logged' | 'missing' | 'due' | 'future';

/**
 * State of a meal cell. `missing` = past day (or today, scheduled time
 * passed) with no log → a gap; `due` = today, inside the lead window, not yet
 * logged; `future` = later than that.
 */
export function mealCellState(
  logged: boolean,
  date: string,
  meal: Pick<MealAnchor, 'minutes'>,
  today: string,
  nowMin: number,
): MealCellState {
  if (logged) return 'logged';
  if (date > today) return 'future';
  if (date < today) return 'missing';
  if (meal.minutes < nowMin) return 'missing';
  if (meal.minutes > nowMin + MEAL_LEAD_MIN) return 'future';
  return 'due';
}

/** True for any Run-* type (coach runs are written as concrete Run-* too). */
export function isRunType(type: string | undefined): boolean {
  return type?.startsWith('Run') === true;
}

export function isPadelType(type: string | undefined): boolean {
  return type === 'Padel';
}

/** An explicit Rest slot. A day with NO slot is unplanned, not rest — the
 *  two read differently everywhere (an unplanned day is never "done"). */
export function isRestType(type: string | undefined): boolean {
  return type === 'Rest';
}
